import { groupBySection, packColumns, type MenuBlock, type MenuColumn } from '../paginate.js';
import type { MenuBoardContent } from '../types.js';
import { ARABIC_LINE, bilingual, type MenuTemplate, type RenderContext } from './common.js';
import {
  TG_FAMILY,
  TG_THEME,
  TG_VARIANTS,
  tgCard,
  tgCardCss,
  tgEmblem,
  tgEmblemCss,
  tgHead,
  tgHeadCss,
  tgHeadHeight,
  tgRows,
  tgStripesCss,
  tgTextHeight,
  tgTitle,
} from './tangerine-split.js';

/**
 * Tangerine, layout C: the stripes as a band across the top with the logo, title and tagline,
 * and under it the dishes as upright cards, a round photo over the name, description and red
 * price, four to a row. For shakes, desserts or sides.
 */

const S = '.mb-t-tangerine-banner';
const PHOTO = 9.5;
const PER_ROW = { landscape: 4, portrait: 3 };

function bandHeight(board: MenuBoardContent): number {
  const both = board.settings.language === 'both';
  return 3 + 4.6 * 1.04 + (both && board.settings.titleAr ? 2.3 * 1.35 * ARABIC_LINE : 0) + (board.brand.tagline ? 1.9 : 0) + 3;
}

function column(board: MenuBoardContent, portrait: boolean): MenuColumn {
  const perRow = PER_ROW[portrait ? 'portrait' : 'landscape'];
  const perLine = portrait ? 26 : 34;
  return {
    capacity: (portrait ? 120 : 67.5) - Math.max(bandHeight(board), 15) - 4,
    blockCost: (section, items) =>
      tgHeadHeight(board, section) + tgRows(items.map((i) => PHOTO + 1 + tgTextHeight(board, i, perLine, 2) + 1.6), perRow) + 0.2,
  };
}

function section(ctx: RenderContext, b: MenuBlock): string {
  return `<div class="tg3-sec">${tgHead(ctx, b)}<div class="tg3-cards">${b.items.map((i) => tgCard(ctx, i, 'tg3-card')).join('')}</div></div>`;
}

export const tangerineBanner: MenuTemplate = {
  id: 'tangerine-banner',
  name: 'Tangerine · Banner and cards',
  description:
    'The diagonal stripes as a band across the top with logo, title and tagline, over upright dish cards: round photo, bold name, description and red price, four to a row. Matches the other Tangerine screens.',
  defaultTheme: TG_THEME,
  variants: TG_VARIANTS,
  cuisines: ['burgers', 'cafe', 'drinks'],
  family: { ...TG_FAMILY, layout: 'Banner and cards' },
  uses: { featured: false, sectionImages: false, brand: true, decorations: [] },

  paginate(board, orientation) {
    return packColumns(groupBySection(board), [column(board, orientation === 'portrait')]);
  },

  render(ctx) {
    const { board } = ctx;
    const body = ctx.page.columns.flat().map((b) => section(ctx, b)).join('');
    return [
      '<div class="tg3-band">',
      tgEmblem(ctx, 'tg3-emblem'),
      `<div class="tg3-words">${tgTitle(ctx, 'tg3-title', ctx.orientation === 'portrait' ? 14 : 24)}`,
      bilingual(ctx, board.brand.tagline, board.brand.taglineAr, 'tg3-tag'),
      '</div></div>',
      `<div class="tg3-body">${body}</div>`,
    ].join('');
  },

  css: `
${tgStripesCss(S, 'tg3-band')}
${S} .tg3-band{flex:0 0 auto;min-height:15em;display:flex;align-items:center;gap:3em;padding:1.5em 3.5em}
${tgEmblemCss(S, 'tg3-emblem', 10)}
${S} .tg3-emblem{flex:0 0 auto}
${S} .tg3-words{flex:1 1 auto;min-width:0}
${S} .tg3-title{font-family:var(--mb-font-heading);font-weight:500;font-size:4.6em;line-height:1.04;text-transform:uppercase;letter-spacing:.01em;
  white-space:nowrap;overflow:hidden;text-overflow:ellipsis;padding-top:.05em}
${S} .tg3-title.mb-ar{line-height:1.35}
${S} .tg3-title.mb-2nd{font-size:2.3em}
${S} .tg3-tag{font-size:1.3em;line-height:1.45;opacity:.95;margin-top:.3em;white-space:nowrap;overflow:hidden;text-overflow:ellipsis}
${S} .tg3-body{flex:1 1 auto;min-height:0;padding:2.5em 3.5em 1.5em}
${tgHeadCss(S)}
${tgCardCss(S)}
${S} .tg3-cards{display:grid;grid-template-columns:repeat(4,minmax(0,1fr));column-gap:3em}
${S}.mb-portrait .tg3-cards{grid-template-columns:repeat(3,minmax(0,1fr));column-gap:2.4em}
${S} .tg3-card{display:flex;flex-direction:column;align-items:center;text-align:center;min-width:0;margin-bottom:1.6em}
${S} .tg3-card .tg-photo{width:${PHOTO}em;height:${PHOTO}em;margin-bottom:1em}
${S} .tg3-card-text{width:100%;min-width:0;display:flex;flex-direction:column;align-items:center}
${S} .tg3-card .tg-name,${S} .tg3-card .tg-name2{max-width:100%}
${S} .tg3-card .tg-desc{-webkit-line-clamp:2}
${S} .tg3-card .tg-price{justify-content:center}
`,
};
